import { useCallback, useEffect, useRef, useState } from 'react';
import { getForm, type FormDetail } from '../api/catalog';
import { ApiError } from '../api/httpClientFactory';

export interface FormDetailState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  form: FormDetail | null;
  error: ApiError | null;
  retry: () => void;
}

function asApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError('UNKNOWN', 0);
}

export function useFormDetail(id: number | null): FormDetailState {
  const [status, setStatus] = useState<FormDetailState['status']>(id === null ? 'idle' : 'loading');
  const [form, setForm] = useState<FormDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [seenId, setSeenId] = useState(id);
  const [seenRetry, setSeenRetry] = useState(retryToken);

  if (seenId !== id || seenRetry !== retryToken) {
    setSeenId(id);
    setSeenRetry(retryToken);
    setError(null);
    if (id === null) {
      setStatus('idle');
      setForm(null);
    } else {
      setStatus('loading');
      setForm(null);
    }
  }

  const generation = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const retry = useCallback(() => {
    setRetryToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (id === null) return;
    const gen = generation.current + 1;
    generation.current = gen;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    getForm(id, controller.signal).then(
      (detail) => {
        if (generation.current !== gen) return;
        setForm(detail);
        setStatus('ready');
      },
      (reason: unknown) => {
        if (generation.current !== gen) return;
        if (reason instanceof ApiError && reason.code === 'SESSION_EXPIRED') return;
        setError(asApiError(reason));
        setStatus('error');
      },
    );

    return () => controller.abort();
  }, [id, retryToken]);

  useEffect(() => {
    return () => {
      generation.current += 1;
      controllerRef.current?.abort();
    };
  }, []);

  return { status, form, error, retry };
}
